'use strict';

window.Promise = window.Promise || require('es6-promise');

const keyDown = currify(keyDown_);

const remove = currify(removeEl);

const BUTTON_OK = ['OK'];
const BUTTON_OK_CANCEL = ['OK', 'Cancel'];

const api = (dialog) => ({
  then: dialog.promise.then.bind(dialog.promise),
  catch: dialog.promise.catch.bind(dialog.promise),
  show: callback => {
    callback && callback({
      close: () => {
        dialog.cancel();
        removeEl(dialog.element, dialog.previousActiveElement);
      }
    });
    return dialog.promise;
  }
});

const smalltalk = {};

smalltalk.alert = (title, msg) => {
    return api(showDialog(title, msg, '', BUTTON_OK, {cancel: false}));
};

smalltalk.prompt = (title, msg, value = '', options) => {
    const type = getType(options);

    const val = String(value)
        .replace(/"/g, '&quot;');

    const valueStr = `<input type="${ type }" value="${ val }" data-name="js-input">`;

    return api(showDialog(title, msg, valueStr, options?.buttons?.length ? options.buttons : BUTTON_OK_CANCEL, options));
};

smalltalk.confirm = (title, msg, options) => {
    return api(showDialog(title, msg, '', options?.buttons?.length ? options.buttons : BUTTON_OK_CANCEL, options));
};

export default smalltalk;

function currify(fn, ...args) {
    const f = (fn) => [
        function(a) {
            return fn(...arguments);
        },
        function(a, b) {
            return fn(...arguments);
        },
        function(a, b, c) {
            return fn(...arguments);
        },
        function(a, b, c, d) {
            return fn(...arguments);
        },
        function(a, b, c, d, e) {
            return fn(...arguments);
        }
    ];

    check(fn);

    if (args.length >= fn.length)
        return fn(...args);

    const again = function() {
        return currify(...[fn, ...args, ...arguments]);
    };

    const count = fn.length - args.length - 1;
    const func = f(again)[count];

    return func || again;
}

function check(fn) {
    if (typeof fn !== 'function')
        throw Error('fn should be function!');
}

function store(value) {
    const data = {
        value
    };

    return function(value) {
        if (!arguments.length)
            return data.value;

        data.value = value;

        return value;
    };
};


function getType(options = {}) {
    const {type} = options;

    if (type === 'password')
        return 'password';

    return 'text';
}

function getTemplate(title, msg, value, buttons) {
    const encodedMsg = msg.replace(/\n/g, '<br>');

    return `<div class="page">
        <div data-name="js-close" class="close-button"></div>
        <header>${ title }</header>
        <div class="content-area">${ encodedMsg }${ value }</div>
        <div class="action-area">
            <div class="button-strip"> ${
                buttons.map((name, i) =>
                    `<button tabindex=${ i } data-name="js-${ name.toLowerCase() }">${ name }</button>`
                ).join('')
            }
            </div>
        </div>
    </div>`;
}

function showDialog(title, msg, value, buttons, options) {
    const previousActiveElement = document.activeElement;
    window.smalltalkIsOpen = true
    const ok = store();
    const cancel = store();

    const dialog = document.createElement('div');
    const closeButtons = options?.buttons?.length ? options.buttons.map(b => b.toLowerCase()) : [
        'cancel',
        'close',
        'ok'
    ];

    const promise = new Promise((resolve, reject) => {
        const noCancel = options && !options.cancel;
        const empty = () => {};

        ok(resolve);
        cancel(noCancel ? empty : reject);
    });

    const tmpl = getTemplate(title, msg, value, buttons);

    dialog.innerHTML = tmpl;
    dialog.className = 'smalltalk';

    document.body.appendChild(dialog);

    setTimeout(function() {
        dialog.classList.add('visible');
    }, 0);

    find(dialog, ['ok', 'input']).forEach((el) =>
        el.focus()
    );

    find(dialog, ['input']).forEach((el) => {
        el.setSelectionRange(0, value.length);
    });

    addListenerAll('click', dialog, closeButtons, (event) =>
        closeDialog(event.target, dialog, ok(), cancel(), previousActiveElement)
    );

    ['click', 'contextmenu'].forEach((event) =>
        dialog.addEventListener(event, () =>
            find(dialog, ['ok', 'input']).forEach((el) =>
                el.focus()
            )
        )
    );

    dialog.addEventListener('keydown', keyDown(dialog, ok(), cancel(), previousActiveElement));

    return {
      element: dialog,
      previousActiveElement: previousActiveElement,
      cancel: cancel(),
      promise,
    };
}

function keyDown_(dialog, ok, cancel, previousActiveElement, event) {
    const KEY   = {
        ENTER : 13,
        ESC   : 27,
        TAB   : 9,
        LEFT  : 37,
        UP    : 38,
        RIGHT : 39,
        DOWN  : 40
    };

    const keyCode = event.keyCode;
    const el = event.target;

    const namesAll = ['ok', 'cancel', 'input'];
    const names = find(dialog, namesAll)
        .map(getDataName);

    switch(keyCode) {
    case KEY.ENTER:
        closeDialog(el, dialog, ok, cancel, previousActiveElement);
        event.preventDefault();
        break;

    case KEY.ESC:
        remove(previousActiveElement);
        cancel();
        break;

    case KEY.TAB:
        if (event.shiftKey)
            tab(dialog, names);

        tab(dialog, names);
        event.preventDefault();
        break;

    default:
        ['left', 'right', 'up', 'down'].filter((name) => {
            return keyCode === KEY[name.toUpperCase()];
        }).forEach(() => {
            changeButtonFocus(dialog, names);
        });

        break;
    }

    event.stopPropagation();
}

function getDataName(el) {
    return el
        .getAttribute('data-name')
        .replace('js-', '');
}

function changeButtonFocus(dialog, names) {
    const active = document.activeElement;
    const activeName = getDataName(active);
    const isButton = /ok|cancel/.test(activeName);
    const count = names.length - 1;
    const getName = (activeName) => {
        if (activeName === 'cancel')
            return 'ok';

        return 'cancel';
    };

    if (activeName === 'input' || !count || !isButton)
        return;

    const name = getName(activeName);

    find(dialog, [name]).forEach((el) => {
        el.focus();
    });
}

const getIndex = (count, index) => {
    if (index === count)
        return 0;

    return index + 1;
};

function tab(dialog, names) {
    const active = document.activeElement;
    const activeName = getDataName(active);
    const count = names.length - 1;

    const activeIndex = names.indexOf(activeName);
    const index = getIndex(count, activeIndex);

    const name = names[index];

    find(dialog, [name]).forEach((el) =>
        el.focus()
    );
}

function closeDialog(el, dialog, ok, cancel, previousActiveElement) {
    window.smalltalkIsOpen = false
    const name = el
        .getAttribute('data-name')
        .replace('js-', '');

    if (/close|cancel/.test(name)) {
        cancel();
        remove(previousActiveElement);
        return;
    }

    const value = find(dialog, ['input'])
        .reduce((value, el) => el.value, null);

    ok(value);
    remove(previousActiveElement);
}

function find(element, names) {
    const notEmpty = (a) => a;
    const elements = names.map((name) =>
        element.querySelector(`[data-name="js-${ name }"]`)
    ).filter(notEmpty);

    return elements;
}

function addListenerAll(event, parent, elements, fn) {
    find(parent, elements)
        .forEach((el) =>
            el.addEventListener(event, fn)
        );
}

function removeEl(previousActiveElement) {
    const el = document.querySelector('.smalltalk');
    el && el.classList.remove('visible');

    window.setTimeout(function() {
      el && el.parentElement && el.parentElement.removeChild(el);
      previousActiveElement && previousActiveElement.focus();
    }, 200);
}

function bind(fn, ...args) {
    return () => fn(...args);
}

